import React, {
	type CSSProperties,
	type DependencyList,
	type ReactNode,
	createContext,
	useContext,
	useEffect,
	useId,
	useRef,
} from "react";

export type WidgetAlert = {
	variant: "warning" | "info";
	shortTitle?: React.ReactNode;
	title?: React.ReactNode;
	content?: React.ReactNode;
};

export type WidgetOptions = {
	actionHeader?: (() => React.ReactNode) | React.ReactNode;
	tooltip?: ReactNode;
	title: React.ReactNode;
	subTitle?: React.ReactNode;
	style?: CSSProperties;
	maxHeight?: number | "none";
	fillHeight?: boolean;
	isLoading?: boolean;
};

export type WidgetBlockContextValue = {
	setWidgetOptions(opts: WidgetOptions): void;
	/** Optional, so a test frame standing in for the mapper can provide only the header it asserts on. */
	setWidgetLoading?(id: string, loading: boolean): void;
	/** Optional, for the same reason as {@link setWidgetLoading}. */
	setWidgetWalled?(id: string, walled: boolean): void;
};

export const WidgetBlockContext = createContext<WidgetBlockContextValue>({
	setWidgetOptions(_opts: WidgetOptions): void {
		// noop
	},
});

export function useWidgetOptions(optsProvider: () => WidgetOptions, deps: DependencyList): void {
	const { setWidgetOptions } = useContext(WidgetBlockContext);
	const prevDepsRef = useRef<DependencyList | undefined>(undefined);
	const prevDeps = prevDepsRef.current;
	if (
		prevDeps === undefined ||
		prevDeps.length !== deps.length ||
		deps.some((dep, i) => !Object.is(dep, prevDeps[i]))
	) {
		prevDepsRef.current = deps;
		setWidgetOptions(optsProvider());
	}
}

/**
 * Publishes a widget's loading state to its card header. Callers are tracked by id, so a card holding
 * more than one stays loading while any of them is.
 */
export function useWidgetLoading(loading: boolean): void {
	const { setWidgetLoading } = useContext(WidgetBlockContext);
	const id = useId();
	useEffect(() => {
		setWidgetLoading?.(id, loading);
		return () => setWidgetLoading?.(id, false);
	}, [id, loading, setWidgetLoading]);
}

/**
 * Publishes that a base has put one of its own walls — the as-of snapshot wall or a failed fetch — in
 * place of the widget's content, so the card stops trusting the options object: the publisher that set
 * it unmounted with that content and cannot withdraw it. Callers are tracked by id, as on the loading
 * channel.
 *
 * A payload's own status wall stays the widget's to handle: controls it publishes live can remain
 * meaningful over it.
 */
export function useWidgetWalled(walled: boolean): void {
	const { setWidgetWalled } = useContext(WidgetBlockContext);
	const id = useId();
	useEffect(() => {
		setWidgetWalled?.(id, walled);
		return () => setWidgetWalled?.(id, false);
	}, [id, walled, setWidgetWalled]);
}
